import { BasicChannel, ColorTarget, Encoding, EncodingResult, MarkType, VariableType } from '@mode-vegas/vegas';
import type * as ngRouter from '@uirouter/angularjs';
import { Observable } from 'rxjs';
import { filter, first, map, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { Actions } from '~/actions';
import deleteExplorationModalTemplate from '~/components/modal-delete-exploration/delete-exploration-modal.html';
import renameExplorationTemplate from '~/components/modal-edit-exploration/edit-exploration-modal.html';
import { ActiveExplorationSelectors } from '~/selectors/active-exploration.selectors';
import { AppStore } from '~/types/main.types';

import { ChartFacade, DragInfo, SelectedChartService } from '@mode/charts/contract';
import { ChartActions, ChartSwitchActions, fromChartSelectors } from '@mode/charts/domain';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ModalService } from '@mode/shared/ui-angularjs';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { ChartDesignerActionType } from '~/actions/chart-designer/designer.actions';
import { ActiveExplorationActionType } from '../../actions/exploration/active-exploration.actions';
import ViewService from '../view-service';
import { WindowMessageService } from '../window-message-service';

import { DateTime } from '@mode-switch/express';
import { peekRedoStack, peekUndoStack } from '@mode/charts/domain';
import { ViewDataModalActions } from '@mode/reports/domain';
import { ChartTypes, DataProfilingTypes } from '@mode/shared/contract-common';
import { ExplorationService } from '@mode/shared/data-access-webapp';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { DataViewFacade } from '@mode/switch-views/contract';
import { ChartViewData } from '~/lib/charts/chart-view-data';
import { updateBigValueTrendLabel } from '~/lib/viz-format-helpers';

export class ExplorationVizService implements SelectedChartService {
  public static $inject = [
    '$state',
    'RxStoreService',
    'actions$',
    'ChartModel',
    'WindowMessageService',
    'ToastService',
    'AnalyticsService',
    'ViewService',
    'ExplorationService',
    '$stateParams',
    'ModalService',
    'DataViewFacade',
    'ChartFacade',
  ];

  private _dragField: Readonly<DragInfo>; // singleton for storing the currently dragged field

  constructor(
    private $state: ngRouter.StateService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private ChartModel: any,
    private wms: WindowMessageService,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private viewService: ViewService,
    private explorationService: ExplorationService,
    private $stateParams: ngRouter.StateParams,
    private modalService: ModalService,
    private dataViewFacade: DataViewFacade,
    private chartFacade: ChartFacade
  ) {
    this.actions$
      .pipe(
        ofType(ActiveExplorationActionType.ViewChartData),
        withLatestFrom(
          this.store.selectNoEmpty(ActiveExplorationSelectors.getQueryToken),
          this.store.selectNoEmpty(ActiveExplorationSelectors.getEncoding),
          this.store.select(ActiveExplorationSelectors.getTitle),
          this.dataViewFacade.model$
        ),
        map(([action, queryToken, encoding, title, model]) =>
          ViewDataModalActions.openModal({
            encoding: ChartViewData.encodingFromTarget(model, queryToken, encoding, action.target),
            seriesDetail: ChartViewData.encodingSeriesDetail(model, queryToken, encoding, action.target),
            queryToken,
            title,
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));
  }

  deleteExploration(org: string, explorationId: string) {
    this.explorationService
      .deleteExploration(org, explorationId)
      .then(() => {
        this.alertService.success('Exploration deleted');
        this.$state.go('reportShow.noRuns.dashboard', {
          account: this.$stateParams['account'],
          reportId: this.$stateParams['reportId'],
        });
      })
      .catch((err) => {
        this.alertService.error('There was a problem deleting your exploration');
      });
  }

  openEditExplorationModal(currentExplorationName: string, currentExplorationDescription: string) {
    const modalAttributes = {
      rename: (newName: string, newDescription: string) => {
        this.updateDetails(FLAMINGO_RESERVED_REFS.Exploration, newName, newDescription);
      },
      header: 'Edit name and description',
      explorationTitle: currentExplorationName,
      vizDescription: currentExplorationDescription,
    };

    const modalOptions = {
      template: renameExplorationTemplate,
      windowClass: 'explore-modal',
      animation: true,
      backdrop: true,
    };

    this.modalService.open(modalAttributes, modalOptions);
  }

  openDeleteExplorationModal(org: string, explorationId: string) {
    const modalAttributes = {
      delete: () => this.deleteExploration(org, explorationId),
      header: 'Delete Exploration',
      body: "Are you sure you want to delete this exploration? This action can't be undone.",
    };

    const modalOptions = {
      template: deleteExplorationModalTemplate,
      windowClass: 'explore-modal',
      animation: true,
      backdrop: true,
    };

    this.modalService.open(modalAttributes, modalOptions);
  }

  /**
   * New Exploration Service Stuff
   *
   * TODO: in the future all methods will take a token, and token will be passed in from context
   */

  public updateTitle(chartToken: string, title: string) {
    this.store.dispatch(
      ChartActions.updateTitle({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        title,
      })
    );
  }

  public updateDescription(chartToken: string, description: string) {
    this.store.dispatch(
      ChartActions.updateDescription({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        description,
      })
    );
  }

  public updateDetails(chartToken: string, title: string, description: string) {
    this.store.dispatch(
      ChartActions.updateDetails({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        title,
        description,
      })
    );
  }

  public updateBigValueDescription(chartToken: string, description: string) {
    this.store.dispatch(
      ChartActions.updateBigValueDescription({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        description,
      })
    );
  }

  public dispatchEncodingUpdate(result: EncodingResult, pillAction?: ChartTypes.PillActionType) {
    this.store.dispatch(
      ChartActions.updateEncoding({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        encoding: result.encoding,
      })
    );
  }

  public updateChartType(chartType: ChartTypes.ChartType) {
    this.store.dispatch(
      ChartActions.updateType({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        chartType,
      })
    );
  }

  public updateIsAutoUpdateDisabled(disable: boolean) {
    this.store.dispatch(
      ChartActions.updateIsAutoUpdateDisabled({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        isAutoUpdateDisabled: disable,
      })
    );
  }

  public manuallyApplyChanges(): void {
    this.store.dispatch(ChartActions.manuallyApplyChanges({ token: FLAMINGO_RESERVED_REFS.Exploration }));
  }

  public updateCaption(chartToken: string, caption: ChartTypes.CaptionInfo) {
    this.store.dispatch(
      ChartActions.updateCaption({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        caption,
      })
    );
  }

  public addFieldAndMakeDual(
    enc: Encoding,
    field: CompiledFormula,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.addFieldAndMakeDual(enc, field, channel, leftMark, rightMark);
    this.dispatchEncodingUpdate(newEncoding);
  }

  public moveFieldAndMakeDual(
    enc: Encoding,
    field: CompiledField,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.moveFieldAndMakeDual(enc, field, channel, leftMark, rightMark);
    this.dispatchEncodingUpdate(newEncoding);
  }

  public removeSort(source: string) {
    this.store.dispatch(
      ChartActions.removeSort({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        formula: source,
      })
    );
  }

  // Chart Formatting Actions
  updateChartFormat(format: ChartTypes.ChartFormat) {
    this.store.dispatch(
      ChartActions.updateFormat({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        format,
      })
    );
  }

  // TODO: this will need to be updated when combining to a single service
  updateBigValueTrendLabel(label: string) {
    this.store
      .select(ActiveExplorationSelectors.getFormat)
      .pipe(
        first(),
        map((format) => updateBigValueTrendLabel(format, label))
      )
      .subscribe((format) => this.updateChartFormat(format));
  }

  updateTextFormat(formula: string, pane: ChartTypes.FieldPaneTypes, format: ChartTypes.TextFormat) {
    this.store.dispatch(
      ChartActions.updateFieldFormat({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        formula,
        format,
        pane,
      })
    );
  }

  updateColorField(
    formula: string,
    varType: VariableType,
    colorPaletteToken = '',
    palette = [],
    series?: string,
    colorMap?: ChartTypes.ColorEntries,
    gradient?: ChartTypes.ContinuousGradient,
    unassignedColor?: string,
    colorTarget?: ColorTarget
  ) {
    this.store.dispatch(
      ChartActions.updateExplorationFieldColor({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        colorPaletteToken,
        palette,
        formula,
        varType,
        series,
        colorMap,
        gradient,
        unassignedColor,
        colorTarget,
      })
    );
  }

  updateAxisFormat(formula: string, varType: VariableType, format: ChartTypes.AxisFieldFormat) {
    this.store.dispatch(
      ChartActions.updateAxisFieldFormat({ token: FLAMINGO_RESERVED_REFS.Exploration, varType, formula, format })
    );
  }

  updatePaneFormat(formula: string, varType: VariableType, format: ChartTypes.PaneFieldFormat) {
    this.store.dispatch(
      ChartActions.updatePaneFieldFormat({ token: FLAMINGO_RESERVED_REFS.Exploration, varType, formula, format })
    );
  }

  updateHeaderFormat(formula: string, varType: VariableType, format: ChartTypes.HeaderFieldFormat) {
    this.store.dispatch(
      ChartActions.updateHeaderFieldFormat({ token: FLAMINGO_RESERVED_REFS.Exploration, varType, formula, format })
    );
  }

  updateLegendFormat(formula: string, varType: VariableType, format: ChartTypes.LegendFieldFormat) {
    this.store.dispatch(
      ChartActions.updateLegendFieldFormat({ token: FLAMINGO_RESERVED_REFS.Exploration, varType, formula, format })
    );
  }

  updateTheme(
    theme: string,
    colorPaletteToken = '',
    custom = [],
    showAlert = true,
    colorMap?: {
      [seriesName: string]: string;
    }
  ) {
    this.store.dispatch(
      ChartActions.updateExplorationColors({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        palette: custom,
        colorPaletteToken: colorPaletteToken,
        showAlert,
        colorMap,
      })
    );
  }

  updateFacetSizes(facetSizes: ChartTypes.FacetSizeMap) {
    this.store.dispatch(ChartActions.updateFacetSizes({ token: FLAMINGO_RESERVED_REFS.Exploration, facetSizes }));
  }

  public dispatchEncodingAndFormatUpdate(
    result: EncodingResult,
    facetSizes?: ChartTypes.FacetSizeMap,
    fieldFormats?: ChartTypes.FieldFormatMap,
    format?: Partial<ChartTypes.ChartFormat>,
    fieldColors?: ChartTypes.FieldColorMap,
    colorPaletteToken?: string
  ) {
    this.store.dispatch(
      ChartActions.updateEncodingAndFormat({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        encoding: result.encoding,
        facetSizes,
        fieldFormats,
        format,
        fieldColors,
        colorPaletteToken,
      })
    );
  }

  public dispatchReplaceChartHistory(encoding: Encoding, isAutoUpdateDisabled: boolean): void {
    this.store.dispatch(
      ChartActions.replaceChartHistory({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        encoding,
        isAutoUpdateDisabled,
      })
    );
  }

  public copyToVizStorage() {
    this.chartFacade.selectedToken$.pipe(first()).subscribe((token) => {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.ExplorationsNav,
        action: 'Copy Chart',
        resource: AnalyticsEvent.Context.Explorations,
        id: token ?? FLAMINGO_RESERVED_REFS.Exploration,
      });
      this.chartFacade.copyToVizStorage(token ?? FLAMINGO_RESERVED_REFS.Exploration);
    });
  }

  clearChart() {
    this.store.dispatch(
      ChartActions.clearChart({
        token: FLAMINGO_RESERVED_REFS.Exploration,
      })
    );
  }

  clearFilters() {
    this.store.dispatch(
      ChartActions.clearFilters({
        token: FLAMINGO_RESERVED_REFS.Exploration,
      })
    );
  }

  clearSorts() {
    this.store.dispatch(
      ChartActions.clearSorts({
        token: FLAMINGO_RESERVED_REFS.Exploration,
      })
    );
  }

  clearFormatting() {
    this.store.dispatch(
      ChartActions.clearFormatting({
        token: FLAMINGO_RESERVED_REFS.Exploration,
      })
    );
  }

  undo(token: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(FLAMINGO_RESERVED_REFS.Exploration),
      (obs) => obs.pipe(filter((history) => peekUndoStack(history) != null)),
      (history) => {
        if (history.undoStack.length > 0) {
          return ChartActions.undo({
            token: FLAMINGO_RESERVED_REFS.Exploration,
            snapshot: peekUndoStack(history),
          });
        }
      }
    );
  }

  redo(token: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(FLAMINGO_RESERVED_REFS.Exploration),
      (obs) => obs.pipe(filter((history) => peekRedoStack(history) != null)),
      (history) => {
        if (history.redoStack.length > 0) {
          return ChartActions.redo({
            token: FLAMINGO_RESERVED_REFS.Exploration,
            snapshot: peekRedoStack(history),
          });
        }
      }
    );
  }

  swapAxes() {
    this.store.dispatch(
      ChartActions.swapAxes({
        token: FLAMINGO_RESERVED_REFS.Exploration,
      })
    );
  }

  quickSort(descending: boolean) {
    this.store.dispatch(
      ChartActions.quickSort({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        descending,
      })
    );
  }

  drillDown(chartToken: string, target: ChartTypes.MarkTarget) {
    this.store.dispatch(
      ChartActions.drilldown({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        target,
      })
    );
  }

  changeHierarchy(token: string, forward: boolean) {
    this.store.dispatch(
      ChartActions.changeHierarchy({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        forward,
      })
    );
  }

  dynamicDrillDown(
    token: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ) {
    this.store.dispatch(
      ChartActions.dynamicDrillDown({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        target,
        targetFieldId,
        drillToField,
        dateTruncUnit,
      })
    );
  }

  public setOffset(offset: number) {
    this.store.dispatch({
      type: ChartDesignerActionType.SetPagingOffset,
      vizToken: FLAMINGO_RESERVED_REFS.Exploration,
      offset: offset,
    });
  }

  minimizeChartPane() {
    this.store.dispatch({
      type: ChartDesignerActionType.MinimizeChartPane,
    });
  }

  maximizeChartPane() {
    this.store.dispatch({
      type: ChartDesignerActionType.MaximizeChartPane,
    });
  }

  minimizeDataPane() {
    this.store.dispatch({
      type: ChartDesignerActionType.MinimizeDataPane,
    });
  }

  maximizeDataPane() {
    this.store.dispatch({
      type: ChartDesignerActionType.MaximizeDataPane,
    });
  }

  startDrag(info: DragInfo) {
    this._dragField = info;
    this.chartFacade.startFieldDrag(info.field, info.source);
  }

  endDrag() {
    this._dragField = undefined;
    this.chartFacade.endFieldDrag();
  }

  get dragField() {
    return this._dragField;
  }

  get readonly(): boolean {
    return false;
  }

  getDataProfiling(data: DataProfilingTypes.DataProfilingChartDetails) {
    this.store.dispatch(
      ChartSwitchActions.getDataProfiling({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        cols: data.cols,
        referenceId: data.referenceId,
      })
    );
  }

  setPageSize(pageSize: number) {
    this.store.dispatch({
      type: ChartDesignerActionType.SetPageSize,
      vizToken: FLAMINGO_RESERVED_REFS.Exploration,
      pageSize,
    });
  }

  getBinSize(token: string, columnName: string) {
    this.store.dispatch(
      ChartSwitchActions.getBinSize({
        token,
        columnName,
      })
    );
  }

  updateBaseFormatDisplayName(formula: string, varType: VariableType, format: ChartTypes.BaseFieldFormat): void {
    this.store.dispatch(
      ChartActions.updateBaseFormatDisplayName({
        token: FLAMINGO_RESERVED_REFS.Exploration,
        format,
        formula,
        varType,
      })
    );
  }
}
